import {ReflexiveField} from './reflexive-field';
import {FieldType} from './field-type.enum';
import {FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import * as moment from 'moment';
import {FieldGroup} from './field-group';
import {delay, skip} from 'rxjs/operators';
import {Subscription} from 'rxjs';

export abstract class ReflexiveRestModel {
  fields: {
    [fieldName: string]: ReflexiveField;
  };
  fieldGroup?: FieldGroup;
  protected formGroup: FormGroup = null;
  // public setFormGroup(formGroup: FormGroup) {
  //   this.formGroup = formGroup;
  inputArrayFieldItemsSubscriptions: Subscription[] = [];

  protected prepareValueForServer(fieldType: FieldType, value: any) {
    switch (fieldType) {
      case FieldType.IntegerField:
        return value !== '' ? parseInt(value, 10) : null;
      case FieldType.FloatField:
        return value !== '' ? parseFloat(value) : null;
      case FieldType.DateTimeField:
        return value ? Math.round((value as Date).getTime() / 1000) : null;
      case FieldType.DurationDaysField:
        return value ? value * 86400 : null;
      case FieldType.DurationMinutesField:
        return  value  ? (parseInt(value, 10) === -1 ? -1 : value * 60) : null;
      case FieldType.DateField:
        const unix = value ? moment.utc(value).unix() : null;
        if (unix) {
          const offset = moment.parseZone(value).utcOffset() * 60;
          return unix + offset;
        }
        return null;
      default:
        return value;
    }
  }
  public prepareDataForRest(fieldNames?: Array<string>): { [key: string]: any; } {
    const fieldNamesToIterate = fieldNames !== undefined ? fieldNames : Object.keys(this.fields);
    const dataForRest = fieldNamesToIterate.reduce(
      (accumulator, fieldName) => {
        if (!this.fields[fieldName]) {
          accumulator[fieldName] = null;
          return accumulator;
        }
        switch (this.fields[fieldName].type) {
          case FieldType.ReflexiveModelArrayField:
            accumulator[fieldName] = this.fields[fieldName].value ?
              (this.fields[fieldName].value as Array<ReflexiveRestModel>).map(
                model => model.prepareDataForRest(this.fields[fieldName].reflexiveModelArrayConfig.fields)
              )
              : [];
            break;
          case FieldType.ReflexiveDragArrayField:
            accumulator[fieldName] = this.fields[fieldName].value ?
              (this.fields[fieldName].value as Array<ReflexiveRestModel>).map(
                model => model.prepareDataForRest(this.fields[fieldName].reflexiveDragArrayConfig.fields)
              )
              : [];
            break;
          case FieldType.ReflexiveModelArrayColumnField:
            accumulator[fieldName] = this.fields[fieldName].value ?
              (this.fields[fieldName].value as Array<ReflexiveRestModel>).map(
                model => model.prepareDataForRest(this.fields[fieldName].reflexiveModelArrayColumnField.fields)
              )
              : [];
            break;
          case FieldType.VariationArrayField:
            accumulator[fieldName] = this.fields[fieldName].value ?
              this.fields[fieldName].value.map(field => field.value) : [];
            break;
          case FieldType.ReflexiveModelObjectField:
            accumulator[fieldName] = this.fields[fieldName].value.prepareDataForRest(this.fields[fieldName].reflexiveModeObjectConfig.fields);
            break;
          case FieldType.InputArrayField:
            accumulator[fieldName] = Object.keys(this.fields[fieldName].value).reduce(
              (prev, key) => {
                prev[key] = this.prepareValueForServer(
                  this.fields[fieldName].inputArray.childFieldConfig.type,
                  this.fields[fieldName].value[key]
                );
                return prev;
              },
              {}
            );
            break;
          default:
            accumulator[fieldName] = this.prepareValueForServer(
              this.fields[fieldName].type,
              this.fields[fieldName].value
            );
            break;
        }
        return accumulator;
      },
      {}
    );
    return dataForRest;
  }
  public loadFromAngularFormGroup(formGroup: FormGroup) {
    Object.keys(this.fields).forEach(
      fieldName => {
        if (formGroup.controls[fieldName]) {
          switch (this.fields[fieldName].type) {
            case FieldType.ReflexiveModelArrayField:
              this.fields[fieldName].value = (formGroup.controls[fieldName] as FormArray).controls.map(
                (childFormGroup: FormGroup) =>
                  this.fields[fieldName].reflexiveModelArrayConfig.loadChildFromAngularFormGroup(childFormGroup)
              );
              break;
            case FieldType.ReflexiveDragArrayField:
              this.fields[fieldName].value = (formGroup.controls[fieldName] as FormArray).controls.map(
                (childFormGroup: FormGroup) =>
                  this.fields[fieldName].reflexiveDragArrayConfig.loadChildFromAngularFormGroup(childFormGroup)
              );
              break;
            case FieldType.ReflexiveModelArrayColumnField:
              this.fields[fieldName].value = (formGroup.controls[fieldName] as FormArray).controls.map(
                (childFormGroup: FormGroup) =>
                  this.fields[fieldName].reflexiveModelArrayColumnField.loadChildFromAngularFormGroup(childFormGroup)
              );
              break;
            case FieldType.VariationArrayField:
              this.fields[fieldName].value = (formGroup.controls[fieldName] as FormArray).controls;
              break;
            case FieldType.ReflexiveModelObjectField:
              this.fields[fieldName].value = this.fields[fieldName].reflexiveModeObjectConfig.loadChildFromAngularFormGroup(formGroup.controls[fieldName] as FormGroup);
              break;
            default:
              this.fields[fieldName].value = formGroup.controls[fieldName].value;
          }
        }
      }
    );
    return this;
  }

  protected convertValueFromServer(fieldType: FieldType, value: any) {
    switch (fieldType) {
      case FieldType.DateTimeField:
        if (value && Number.isInteger(value)) {
          if (value > 17500000000) { // Fri Jul 21 2524
            return new Date(value);
          } else {
            return new Date(value * 1000);
          }
        }
        return value ? new Date(value) : undefined;
      case FieldType.DateField:
        return value ? new Date(value) : undefined;
      case FieldType.DurationDaysField:
        return value !== null && value !== undefined ? value / 86400 : undefined;
      case FieldType.DurationMinutesField:
        return value !== null && value !== undefined ? (parseInt(value, 10) === -1 ? -1 : value / 60) : undefined;
      default:
        return value;
    }
  }
  public loadFromServerData(data: any) {
    Object.keys(this.fields).forEach(
      fieldName => {
        switch (this.fields[fieldName].type) {
          case FieldType.ReflexiveModelArrayField:
            this.fields[fieldName].value = data[fieldName] && Array.isArray(data[fieldName]) ?
              data[fieldName].map(
                item => this.fields[fieldName].reflexiveModelArrayConfig.loadChildFromServerData(item)
              ) : undefined;
            break;
          case FieldType.ReflexiveDragArrayField:
            this.fields[fieldName].value = data[fieldName] && Array.isArray(data[fieldName]) ?
              data[fieldName].map(
                item => this.fields[fieldName].reflexiveDragArrayConfig.loadChildFromServerData(item)
              ) : undefined;
            break;
          case FieldType.ReflexiveModelArrayColumnField:
            this.fields[fieldName].value = data[fieldName] && Array.isArray(data[fieldName]) ?
              data[fieldName].map(
                item => this.fields[fieldName].reflexiveModelArrayColumnField.loadChildFromServerData(item)
              ) : undefined;
            break;
          case FieldType.VariationArrayField:
            this.fields[fieldName].value = data[fieldName] && Array.isArray(data[fieldName]) ?
              data[fieldName] : undefined;
            break;
          case FieldType.ReflexiveModelObjectField:
            this.fields[fieldName].value = data[fieldName] ?
              this.fields[fieldName].reflexiveModeObjectConfig.loadChildFromServerData(data[fieldName])
              : undefined;
            break;
          case FieldType.InputArrayField:
            this.fields[fieldName].value = data[fieldName] && (typeof data[fieldName] === 'object') ?
              Object.keys(data[fieldName]).reduce(
                (prev, key) => {
                  prev[key] = this.convertValueFromServer(
                    this.fields[fieldName].inputArray.childFieldConfig.type,
                    data[fieldName][key]
                  );
                  return prev;
                },
                {}
              )
              : {};
            break;
          default:
            this.fields[fieldName].value = this.convertValueFromServer(this.fields[fieldName].type, data[fieldName]);
            break;
        }
      }
    );
    return this;
  }
  public getFormGroup(formBuilder: FormBuilder, fieldNames?: Array<string>): FormGroup {
    if (!this.formGroup) {
      const fieldNamesToIterate = fieldNames !== undefined ? fieldNames : Object.keys(this.fields);
      const formGroupConfig = fieldNamesToIterate.reduce(
        (accumulator, fieldName) => {
          if (this.fields.hasOwnProperty(fieldName)) {
            const syncValidators = this.fields[fieldName].validators ?
              Validators.compose(this.fields[fieldName].validators) : [];
            const asyncValidators = this.fields[fieldName].asyncValidators ?
              Validators.composeAsync(this.fields[fieldName].asyncValidators) : undefined;
            switch (this.fields[fieldName].type) {
              case FieldType.ReflexiveModelArrayField:
                if (this.fields[fieldName].value && this.fields[fieldName].value.length) {
                  accumulator[fieldName] = formBuilder.array(
                    this.fields[fieldName].value.map(c => c.getFormGroup(
                      formBuilder,
                      this.fields[fieldName].reflexiveModelArrayConfig
                      && this.fields[fieldName].reflexiveModelArrayConfig.fields ?
                        this.fields[fieldName].reflexiveModelArrayConfig.fields : undefined)),
                    syncValidators,
                    asyncValidators
                  );
                  return accumulator;
                } else {
                  accumulator[fieldName] = formBuilder.array([], syncValidators, asyncValidators);
                }
                break;
              case FieldType.ReflexiveDragArrayField:
                if (this.fields[fieldName].value && this.fields[fieldName].value.length) {
                  accumulator[fieldName] = formBuilder.array(
                    this.fields[fieldName].value.map(c => c.getFormGroup(
                      formBuilder,
                      this.fields[fieldName].reflexiveDragArrayConfig
                      && this.fields[fieldName].reflexiveDragArrayConfig.fields ?
                        this.fields[fieldName].reflexiveDragArrayConfig.fields : undefined)),
                    syncValidators,
                    asyncValidators
                  );
                  return accumulator;
                } else {
                  accumulator[fieldName] = formBuilder.array([], syncValidators, asyncValidators);
                }
                break;
              case FieldType.ReflexiveModelArrayColumnField:
                if (this.fields[fieldName].value && this.fields[fieldName].value.length) {
                  accumulator[fieldName] = formBuilder.array(
                    this.fields[fieldName].value.map(c => c.getFormGroup(
                      formBuilder,
                      this.fields[fieldName].reflexiveModelArrayColumnField
                      && this.fields[fieldName].reflexiveModelArrayColumnField.fields ?
                        this.fields[fieldName].reflexiveModelArrayColumnField.fields : undefined)),
                    syncValidators,
                    asyncValidators
                  );
                  return accumulator;
                } else {
                  accumulator[fieldName] = formBuilder.array([], syncValidators, asyncValidators);
                }
                break;
              case FieldType.VariationArrayField:
                if (this.fields[fieldName].value && this.fields[fieldName].value.length) {
                  accumulator[fieldName] = formBuilder.array(
                    this.fields[fieldName].value.map(c => formBuilder.control(
                      c ? c : '',
                      syncValidators,
                      asyncValidators
                    )),
                    syncValidators,
                    asyncValidators
                  );
                  return accumulator;
                } else {
                  accumulator[fieldName] = formBuilder.array([], syncValidators, asyncValidators);
                }
                break;
              case FieldType.ReflexiveModelObjectField:
                if (this.fields[fieldName].value) {
                  accumulator[fieldName] = this.fields[fieldName].value.getFormGroup(
                    formBuilder,
                    this.fields[fieldName].reflexiveModeObjectConfig
                    && this.fields[fieldName].reflexiveModeObjectConfig.fields ?
                      this.fields[fieldName].reflexiveModeObjectConfig.fields : undefined);
                  return accumulator;
                } else {
                  this.fields[fieldName].value = this.fields[fieldName].reflexiveModeObjectConfig.createChild();
                  accumulator[fieldName] = this.fields[fieldName].value.getFormGroup(
                    formBuilder,
                    this.fields[fieldName].reflexiveModeObjectConfig
                    && this.fields[fieldName].reflexiveModeObjectConfig.fields ?
                      this.fields[fieldName].reflexiveModeObjectConfig.fields : undefined);
                }
                break;
              case FieldType.InputArrayField:
                const childSyncValidators = this.fields[fieldName].inputArray.childFieldConfig.validators ?
                  Validators.compose(this.fields[fieldName].inputArray.childFieldConfig.validators) : [];
                const childAsyncValidators = this.fields[fieldName].inputArray.childFieldConfig.asyncValidators ?
                  Validators.composeAsync(this.fields[fieldName].inputArray.childFieldConfig.asyncValidators) : undefined;
                const inputArrayItems = this.fields[fieldName].inputArray.items.getValue().map(
                  item => item[this.fields[fieldName].inputArray.keyField || 'id']
                );
                const rawInitialValue = this.fields[fieldName].value;
                const initialValue = inputArrayItems.reduce(
                  (prev, inputArrayItem) => {
                    prev[inputArrayItem] = rawInitialValue && inputArrayItem in rawInitialValue ?
                      rawInitialValue[inputArrayItem] : null;
                    return prev;
                  },
                  {}
                );
                accumulator[fieldName] = formBuilder.group(
                  inputArrayItems.reduce(
                    (prev, immutableListItem) => {
                      prev[immutableListItem] = formBuilder.control(
                        initialValue[immutableListItem],
                        childSyncValidators,
                        childAsyncValidators
                      );
                      return prev;
                    },
                    {}
                  )
                );
                this.inputArrayFieldItemsSubscriptions.push(
                  this.fields[fieldName].inputArray.items
                    .pipe(
                      delay(0),
                      skip(1),
                    )
                    .subscribe(
                      items => {
                        // if (!this.formGroup.controls[fieldName]) {
                        //   this.formGroup.controls[fieldName] = new FormGroup({});
                        // }
                        const subFormGroup = this.formGroup.controls[fieldName] as FormGroup;
                        console.log(subFormGroup);
                        const controlNames = Object.keys(subFormGroup.controls);
                        controlNames.forEach(n => subFormGroup.removeControl(n));

                        // const childSyncValidators = this.fields[fieldName].inputArray.childFieldConfig.validators ?
                        //   Validators.compose(this.fields[fieldName].inputArray.childFieldConfig.validators) : [];
                        // const childAsyncValidators = this.fields[fieldName].inputArray.childFieldConfig.asyncValidators ?
                        //   Validators.composeAsync(this.fields[fieldName].inputArray.childFieldConfig.asyncValidators) : undefined;
                        const inputArrayItems2 = (items || []).map(
                          item => item[this.fields[fieldName].inputArray.keyField || 'id']
                        );
                        // const rawInitialValue = this.fields[fieldName].value;
                        inputArrayItems2.forEach(
                          fn => subFormGroup.addControl(
                            fn,
                            new FormControl(
                              rawInitialValue[fn] || null,
                              childSyncValidators,
                              childAsyncValidators
                            )
                          )
                        );
                      }
                    )
                );
                break;
              default:
                accumulator[fieldName] = formBuilder.control(
                  this.fields[fieldName].value !== null ? this.fields[fieldName].value : '',
                  syncValidators,
                  asyncValidators
                );
            }
          }
          return accumulator;
        },
        {}
      );
      this.formGroup = formBuilder.group(formGroupConfig, {validators: []});
    }
    return this.formGroup;
  }

  public getFormGroupConfig(fieldNames?: Array<string>): { [key: string]: any; } {
    const fieldNamesToIterate = fieldNames !== undefined ? fieldNames : Object.keys(this.fields);
    const formGroupConfig = fieldNamesToIterate.reduce(
      (accumulator, fieldName) => {
        if (this.fields.hasOwnProperty(fieldName)) {
          accumulator[fieldName] = [this.fields[fieldName].value !== null ? this.fields[fieldName].value : ''];
          if (this.fields[fieldName].validators) {
            accumulator[fieldName].push(Validators.compose(this.fields[fieldName].validators));
          }
        } else {
          console.error('FIELD ' + fieldName + ' NOT DESCRIBED IN REFLEXIVE MODEL');
        }
        return accumulator;
      },
      {}
    );
    return formGroupConfig;
  }
  public getFieldNames(): Array<string> {
    return Object.keys(this.fields);
  }
  public getField(fieldName): ReflexiveField {
    return this.fields[fieldName];
  }
  public getLabel(fieldName: string): string {
    const label = this.fields[fieldName] ? this.fields[fieldName].label : 'FIELD NOT FOUND';
    return label;
  }

  // private inputArrayField() {
  //   const childSyncValidators = this.fields[fieldName].inputArray.childFieldConfig.validators ?
  //     Validators.compose(this.fields[fieldName].inputArray.childFieldConfig.validators) : [];
  //   const childAsyncValidators = this.fields[fieldName].inputArray.childFieldConfig.asyncValidators ?
  //     Validators.composeAsync(this.fields[fieldName].inputArray.childFieldConfig.asyncValidators) : undefined;
  //   const inputArrayItems = this.fields[fieldName].inputArray.items.getValue().map(
  //     item => item[this.fields[fieldName].inputArray.keyField || 'id']
  //   );
  //   const rawInitialValue = this.fields[fieldName].value;
  //   const initialValue = inputArrayItems.reduce(
  //     (prev, inputArrayItem) => {
  //       prev[inputArrayItem] = rawInitialValue && inputArrayItem in rawInitialValue ?
  //         rawInitialValue[inputArrayItem] : null;
  //       return prev;
  //     },
  //     {}
  //   );
  //   accumulator[fieldName] = formBuilder.group(
  //     inputArrayItems.reduce(
  //       (prev, immutableListItem) => {
  //         prev[immutableListItem] = formBuilder.control(
  //           initialValue[immutableListItem],
  //           childSyncValidators,
  //           childAsyncValidators
  //         );
  //         return prev;
  //       },
  //       {}
  //     )
  //   );
  // }
  public onDestroy() {
    this.inputArrayFieldItemsSubscriptions.filter(s => !!s).forEach(s => s.unsubscribe());
  }

  protected clearFormFields(formFieldsNames: string[], clearValue: any) {
    formFieldsNames
      .filter(n => this.formGroup && this.formGroup.controls[n])
      .forEach(n => this.formGroup.controls[n].setValue(clearValue));
  }

  protected clearFormArrayFields(formFieldsNames: string[]) {
    formFieldsNames
      .filter(n => this.formGroup && this.formGroup.controls[n])
      .forEach(n => {
        const formArray = this.formGroup.controls[n] as FormArray;
        while (formArray.length > 0) {
          formArray.removeAt(0);
        }
      });
  }

  protected getFieldValueIfFieldExists(formFieldsName: string) {
    return this.formGroup && this.formGroup.controls[formFieldsName] ?
      this.formGroup.controls[formFieldsName].value : null;
  }

  protected isFieldValueEqual(formFieldsName: string, expected: any) {
    const control = this.formGroup && this.formGroup.controls[formFieldsName];
    return !!control && control.value === expected;
  }
}
